import { useCallback, useRef } from 'react'
import * as Sentry from '@sentry/browser'

import utils from '@/utils/utils'
import { setSession } from '@/utils/session'

import { AUTH_STEPS } from '../constants/auth-steps'
import { getFingerprint } from '../services/fingerprint-service'
import { authApi } from '../services/auth-api'
import { authAnalytics } from '../services/auth-analytics'
import { redirectIfBannedUser } from '../services/auth-redirect'
import {
  idleSubmitting,
  pendingEmailSubmitting,
  pendingFacebookSubmitting,
} from '../utils/submitting'
import { clearRememberLogin, persistRememberLogin } from '../utils/remember-login'

export function useAuthActions ({
  state,
  setState,
  setUser,
  turnstileToken,
  resetTurnstile,
  closeSessionExpired,
  redirectAfterSuccess,
  pendingCredentials,
}) {
  const callbackRef = useRef(() => {})
  const turnstileTokenRef = useRef(turnstileToken)
  turnstileTokenRef.current = turnstileToken

  const { setPendingCredentials, getPendingCredentials, clearPendingCredentials } = pendingCredentials

  const sendCredentials = useCallback(async (
    username,
    password,
    callback,
    turnstileTokenOverride,
    rememberMe
  ) => {
    const token = turnstileTokenOverride ?? turnstileTokenRef.current

    if (!token) return

    const fingerprintData = await getFingerprint()

    if (callback) callbackRef.current = callback

    setPendingCredentials({ username, password })

    setState({ submittingState: pendingEmailSubmitting() })
    closeSessionExpired()

    try {
      const response = await authApi.login(username, password, fingerprintData, token)
      const responseUser = response?.data?.user

      if (responseUser && redirectIfBannedUser(responseUser)) {
        setState({ submittingState: idleSubmitting() })
        return
      }

      utils.checkAxiosAllErrors([response])

      if (!response || !response.data?.success) {
        resetTurnstile()

        if (response?.data?.code === 0) {
          return setState({
            submittingState: idleSubmitting(),
            sessionsData: response.data,
            loginStatus: AUTH_STEPS.CLOSE_ANOTHER_SESSIONS,
            isSessionExpired: false,
          })
        }

        if (response?.data?.errors?.length > 0) {
          return setState({
            submittingState: idleSubmitting(),
            isFailedLogin: true,
            errorList: response.data.errors,
          })
        }

        return setState({
          submittingState: idleSubmitting(),
          isFailedLogin: true,
          loginStatus: AUTH_STEPS.LOGIN_FORM,
        })
      }

      clearPendingCredentials()

      if (responseUser) {
        utils.setUserInfo(response.data)
        setUser(responseUser)
      }

      setSession(response.data.jwtoken)

      setState({
        loginStatus: null,
        isLoginSuccess: true,
        isAuthenticated: true,
        jwt: response.data.jwtoken,
        submittingState: idleSubmitting(),
        isFailedLogin: false,
      })

      redirectAfterSuccess()

      if (rememberMe === true) {
        await persistRememberLogin(username, password)
      } else if (rememberMe === false) {
        clearRememberLogin()
      }

      try {
        const responseUserData = await authApi.fetchUser()
        authAnalytics.onLoginSuccess(responseUserData.data)
        authAnalytics.onAuthComplete(responseUserData.data)
        utils.setUserInfo(responseUserData.data)
        setState({ userData: responseUserData })
      } catch (fetchError) {
        Sentry.captureException(fetchError)
      }
    } catch (error) {
      console.error(error)
      resetTurnstile()
      setState({ submittingState: idleSubmitting(), isFailedLogin: true })
    }
  }, [
    clearPendingCredentials,
    closeSessionExpired,
    redirectAfterSuccess,
    resetTurnstile,
    setPendingCredentials,
    setState,
    setUser,
  ])

  const responseFacebook = useCallback(async (fbResponse, callback) => {
    const fingerprintData = await getFingerprint()

    if (callback) callbackRef.current = callback

    if (!fbResponse.userID && !fbResponse.accessToken) {
      return console.error('response dont include userId and token')
    }

    setPendingCredentials({ fbResponse })

    setState({ submittingState: pendingFacebookSubmitting() })
    closeSessionExpired()

    try {
      const res = await authApi.facebookLogin(
        fbResponse.userID,
        fbResponse.accessToken,
        fingerprintData
      )

      if (res instanceof Error || !res.data) {
        return setState({
          fbFailed: true,
          loginStatus: AUTH_STEPS.COMPLETE_FACEBOOK_DATA,
          fbData: fbResponse,
          submittingState: idleSubmitting(),
        })
      }

      if (res.data.code === 0 && res.data.sessions?.length > 0) {
        return setState({
          submittingState: idleSubmitting(),
          sessionsData: res.data,
          loginStatus: AUTH_STEPS.CLOSE_ANOTHER_SESSIONS,
          isSessionExpired: false,
        })
      }

      authAnalytics.onLoginSuccess(res.data)
      authAnalytics.onAuthComplete(res.data)

      if (res.data.user && redirectIfBannedUser(res.data.user)) {
        setState({ submittingState: idleSubmitting() })
        return
      }

      clearPendingCredentials()

      utils.setUserInfo(res.data)
      setUser(res.data.user)
      setSession(res.data.jwtoken)

      setState({
        submittingState: idleSubmitting(),
        isFailedLogin: false,
        isAuthenticated: true,
        isLoginFacebookSuccess: true,
        jwt: res.data.jwtoken,
        loginStatus: null,
        userData: res,
      })

      redirectAfterSuccess()
      callbackRef.current(res.data)
    } catch (error) {
      Sentry.captureException(error)
      setState({ submittingState: idleSubmitting() })
    }
  }, [
    clearPendingCredentials,
    closeSessionExpired,
    redirectAfterSuccess,
    setPendingCredentials,
    setState,
  ])

  const handleModalSessions = useCallback(() => {
    const savedTurnstileToken = turnstileTokenRef.current
    const { fbResponse, username, password } = getPendingCredentials()

    // Reintentar login sin volver al formulario — evita doble submit y modal en loading
    setState({
      submittingState: pendingEmailSubmitting(),
      isFailedLogin: false,
    })

    if (fbResponse) {
      responseFacebook(fbResponse)
    } else {
      sendCredentials(username, password, undefined, savedTurnstileToken)
    }
  }, [getPendingCredentials, responseFacebook, sendCredentials, setState])

  const onCloseAllSessionsClicked = useCallback(async () => {
    const sessionToken = state.sessionsData?.token
    setState({ submittingState: pendingEmailSubmitting() })

    try {
      const response = await authApi.closeAllSessions(sessionToken)
      if (response.data.success) {
        handleModalSessions()
      } else {
        setState({ submittingState: idleSubmitting(), closeSessionsStatus: false })
      }
    } catch (error) {
      setState({ submittingState: idleSubmitting(), closeSessionsStatus: false })
    }
  }, [handleModalSessions, setState, state.sessionsData])

  const signupEmail = useCallback((data, callback) => {
    setState({ submittingState: pendingEmailSubmitting() })
    if (callback) callbackRef.current = callback

    authApi.register(data)
      .then((response) => {
        if (response?.data?.success) {
          authAnalytics.onRegisterSuccess(response.data)
          authAnalytics.onAuthComplete(response.data)

          utils.setShowGameFreeRoll(0, true)
          utils.setUserInfo(response.data)
          utils.setToken(response.data.jwtoken)
          setUser(response.data.user)
          setSession(response.data.jwtoken)

          setState({
            isAuthenticated: true,
            loginStatus: null,
            submittingState: idleSubmitting(),
            jwt: response.data.jwtoken,
          })

          callbackRef.current(response)
        } else {
          Sentry.captureException(new Error(JSON.stringify(response.data.errors)), {
            extra: 'Error In registro form',
          })
          setState({ submittingState: idleSubmitting() })
          callbackRef.current(response)
        }
      })
      .catch((error) => {
        Sentry.captureException(error, { extra: 'Error Posting Register' })
        setState({ submittingState: idleSubmitting() })
      })
  }, [setState, setUser])

  const postFacebookRegister = useCallback(async (formData, callback) => {
    setState({ submittingState: pendingFacebookSubmitting() })

    try {
      const response = await authApi.facebookRegister(formData)

      if (response.data.success) {
        authAnalytics.onRegisterSuccess(response.data)
        authAnalytics.onAuthComplete(response.data)

        utils.setUserInfo(response.data)
        setUser(response.data.user)
        setSession(response.data.jwtoken)

        setState({
          isAuthenticated: true,
          submittingState: idleSubmitting(),
          loginStatus: null,
          jwt: response.data.jwtoken,
        })
      } else {
        callback(response?.data?.errors || {})
      }
    } catch (error) {
      Sentry.captureException(error)
    }

    setState({ submittingState: idleSubmitting() })
  }, [setState, setUser])

  return {
    sendCredentials,
    responseFacebook,
    handleModalSessions,
    onCloseAllSessionsClicked,
    signupEmail,
    postFacebookRegister,
    clearPendingCredentials,
  }
}
